import { memo, type FC, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { FileInput } from "@/components/input/FileInput/FileInput";

const DEFAULT_MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

type CsvUploadDropzoneProps = {
  id: string;
  onFilesAccepted: (files: File[]) => void;
  maxSizeBytes?: number;
  disableMaxSize?: boolean;
  maxFiles?: number;
  icon?: ReactNode;
  title?: ReactNode;
  helperText?: ReactNode;
  children?: ReactNode;
};

export const CsvUploadDropzone: FC<CsvUploadDropzoneProps> = memo(
  ({
    id,
    onFilesAccepted,
    maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
    disableMaxSize = false,
    maxFiles,
    icon = <CloudUploadIcon sx={{ fontSize: 40, color: "action.active" }} />,
    title = "Drag and drop your CSV file, or click here to upload",
    helperText = "Max size: 5MB",
    children,
  }) => {
    const titleNode =
      typeof title === "string" || typeof title === "number" ? (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {title}
        </Typography>
      ) : (
        title
      );

    return (
      <Box sx={{ px: 2, py: 2 }}>
        <FileInput
          id={id}
          acceptedFileTypes={["text/csv"]}
          acceptedFileExtensions={[".csv"]}
          // Optional by feature: some uploads (e.g. uploaded accounts) should
          // accept larger CSVs and rely on server-side validation.
          maxSize={disableMaxSize ? undefined : maxSizeBytes}
          maxFiles={maxFiles}
          onFilesAccepted={onFilesAccepted}
          customContent={
            <Stack spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
              {icon}
              {titleNode}
              {helperText !== null && helperText !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  {helperText}
                </Typography>
              )}
              {children}
            </Stack>
          }
        />
      </Box>
    );
  }
);

CsvUploadDropzone.displayName = "CsvUploadDropzone";
